// ============================================================================
// ATS CHECKER COMPONENT
// Client-side resume ATS score checker with full privacy
// ============================================================================

import React, { useState } from 'react';
import { Upload, FileText, AlertCircle, CheckCircle, TrendingUp, Award, Shield, Info, Lock, Sparkles, Loader } from 'lucide-react';
import { parseResume } from '../utils/resumeParser';
import { extractKeywords } from '../utils/keywordMatcher';
import { calculateATSScore, getATSScoreExplanation } from '../utils/atsScorer';

const ATSChecker = ({ isMobile, initialResumeData = null, onNavigate, onOpenAuth }) => {
  const [resumeText, setResumeText] = useState('');
  const [jobDescription, setJobDescription] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [aiEnhancement, setAiEnhancement] = useState(null);
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [showExplanation, setShowExplanation] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [currentCheck, setCurrentCheck] = useState('');

  // Handle file upload
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setError('');
    setIsAnalyzing(true);

    try {
      const text = await parseResume({ file });
      setResumeText(text);
      setIsAnalyzing(false);
    } catch (err) {
      setError(err.message);
      setIsAnalyzing(false);
    }
  };

  // Handle paste text
  const handlePasteText = (event) => {
    setResumeText(event.target.value);
    setError('');
  };

  // Analyze resume with progress tracking
  const analyzeResume = async () => {
    if (!resumeText) {
      setError('Please provide a resume to analyze');
      return;
    }

    setIsAnalyzing(true);
    setError('');
    setResults(null);
    setAnalysisProgress(0);

    const checks = [
      { message: 'Parsing resume content...', progress: 10, delay: 300 },
      { message: 'Analyzing document structure...', progress: 20, delay: 400 },
      { message: 'Detecting resume sections...', progress: 30, delay: 350 },
      { message: 'Checking formatting compliance...', progress: 45, delay: 400 },
      { message: 'Extracting keywords and skills...', progress: 60, delay: 450 },
      { message: 'Evaluating content quality...', progress: 75, delay: 400 },
      { message: 'Calculating ATS compatibility score...', progress: 90, delay: 350 },
      { message: 'Generating recommendations...', progress: 95, delay: 300 },
      { message: 'Finalizing analysis...', progress: 100, delay: 200 }
    ];

    try {
      // Run through all checks with progress updates
      for (const check of checks) {
        setCurrentCheck(check.message);
        setAnalysisProgress(check.progress);
        await new Promise(resolve => setTimeout(resolve, check.delay));
      }

      // Perform actual analysis
      const keywords = jobDescription ? extractKeywords(jobDescription) : null;
      const score = calculateATSScore(resumeText, jobDescription, keywords);

      setResults(score);
      setCurrentCheck('Analysis complete!');

      // Brief delay before clearing progress
      setTimeout(() => {
        setIsAnalyzing(false);
        setAnalysisProgress(0);
        setCurrentCheck('');
      }, 500);
    } catch (err) {
      setError('Error analyzing resume. Please try again.');
      setIsAnalyzing(false);
      setAnalysisProgress(0);
      setCurrentCheck('');
    }
  };

  // Clear analysis
  const clearAnalysis = () => {
    setResumeText('');
    setJobDescription('');
    setResults(null);
    setError('');
    setAiEnhancement(null);
  };

  // Handle AI Improvement
  const handleImproveWithAI = async () => {
    const token = localStorage.getItem('auth_token');

    if (!token) {
      // Not logged in - show auth modal or redirect to pricing
      if (onOpenAuth) {
        onOpenAuth();
      } else {
        window.location.href = '/pricing?message=' + encodeURIComponent('Please sign in to access AI-powered resume improvements');
      }
      return;
    }

    setIsEnhancing(true);
    setError('');

    try {
      // Check if user has PRO subscription from API
      const subResponse = await fetch('https://theresumex.cv/api/user/subscription', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const subData = await subResponse.json();
      const hasCredits = subData.subscription && subData.subscription.remainingAnalyzes > 0;

      if (!hasCredits) {
        setIsEnhancing(false);
        // No PRO or no credits - redirect to pricing
        if (onNavigate) {
          onNavigate('pricing');
          setTimeout(() => {
            window.history.pushState({}, '', '?message=' + encodeURIComponent('You need to be a PRO member to access this feature. Purchase AI analyzes to continue.'));
            window.dispatchEvent(new Event('popstate'));
          }, 100);
        } else {
          window.location.href = '/pricing?message=' + encodeURIComponent('You need to be a PRO member to access this feature. Purchase AI analyzes to continue.');
        }
        return;
      }

      // Has PRO - navigate to AI Analyzer with resume data
      setIsEnhancing(false);

      // Store resume data in localStorage for AI Analyzer to use
      localStorage.setItem('pending_ai_analysis', JSON.stringify({
        resumeText,
        jobDescription: jobDescription || ''
      }));

      // Navigate to AI Analyzer
      if (onNavigate) {
        onNavigate('ai-analyzer');
      } else {
        window.location.href = '/ai-analyzer';
      }

    } catch (err) {
      setError(err.message || 'Failed to check PRO status');
    } finally {
      setIsEnhancing(false);
    }
  };

  // Load from builder
  React.useEffect(() => {
    if (initialResumeData) {
      const resumeString = convertResumeToText(initialResumeData);
      setResumeText(resumeString);
    }
  }, [initialResumeData]);

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: isMobile ? '20px' : '40px' }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '40px' }}>
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '12px',
          background: 'linear-gradient(135deg, #145A64 0%, #1A8A99 100%)',
          padding: '16px 32px',
          borderRadius: '12px',
          marginBottom: '20px',
          boxShadow: '0 4px 12px rgba(20, 90, 100, 0.2)'
        }}>
          <Award size={28} color="white" />
          <h1 style={{
            fontSize: isMobile ? '24px' : '32px',
            fontWeight: '700',
            color: 'white',
            margin: '0',
            fontFamily: '"DM Serif Display", serif'
          }}>
            ATS Resume Checker
          </h1>
        </div>

        <p style={{
          fontSize: isMobile ? '16px' : '18px',
          color: '#5A7C82',
          maxWidth: '700px',
          margin: '0 auto 16px auto',
          lineHeight: '1.6'
        }}>
          Get an instant ATS score for your resume. All analysis happens on your device - your resume never leaves your browser.
        </p>

        {/* Privacy Badge */}
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          background: 'rgba(20, 90, 100, 0.08)',
          padding: '8px 16px',
          borderRadius: '20px'
        }}>
          <Shield size={16} color="#145A64" />
          <span style={{ fontSize: '13px', fontWeight: '600', color: '#145A64' }}>
            100% Private - No Uploads to Any Server - No AI
          </span>
        </div>
      </div>

      {/* Input Section */}
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: isMobile ? '24px' : '32px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        marginBottom: '32px',
        border: '1px solid rgba(20, 90, 100, 0.1)'
      }}>
        <h2 style={{
          fontSize: '20px',
          fontWeight: '600',
          color: '#145A64',
          marginBottom: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <FileText size={20} />
          Step 1: Upload or Paste Your Resume
        </h2>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          marginBottom: '20px',
          fontSize: '13px',
          color: '#059669',
          fontWeight: '500'
        }}>
          <Lock size={14} />
          <span>All processing happens on your device - no server uploads</span>
        </div>

        {/* File Upload */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{
            display: 'block',
            width: '100%',
            padding: '40px 20px',
            border: '2px dashed rgba(20, 90, 100, 0.3)',
            borderRadius: '12px',
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            background: 'rgba(20, 90, 100, 0.02)'
          }}
            onDragOver={(e) => {
              e.preventDefault();
              e.currentTarget.style.borderColor = '#145A64';
              e.currentTarget.style.background = 'rgba(20, 90, 100, 0.05)';
            }}
            onDragLeave={(e) => {
              e.currentTarget.style.borderColor = 'rgba(20, 90, 100, 0.3)';
              e.currentTarget.style.background = 'rgba(20, 90, 100, 0.02)';
            }}
            onDrop={(e) => {
              e.preventDefault();
              e.currentTarget.style.borderColor = 'rgba(20, 90, 100, 0.3)';
              e.currentTarget.style.background = 'rgba(20, 90, 100, 0.02)';
              const file = e.dataTransfer.files[0];
              if (file) {
                handleFileUpload({ target: { files: [file] } });
              }
            }}
          >
            <div style={{ position: 'relative', display: 'inline-block', marginBottom: '12px' }}>
              <Upload size={32} color="#145A64" />
              <div style={{
                position: 'absolute',
                top: '-4px',
                right: '-4px',
                background: '#10B981',
                borderRadius: '50%',
                padding: '3px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}>
                <Lock size={12} color="white" />
              </div>
            </div>
            <div style={{ fontSize: '16px', fontWeight: '600', color: '#145A64', marginBottom: '4px' }}>
              Click to upload or drag and drop
            </div>
            <div style={{ fontSize: '14px', color: '#7A9CA3', marginBottom: '8px' }}>
              PDF or DOCX (Max 5MB)
            </div>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '12px',
              color: '#059669',
              background: 'rgba(16, 185, 129, 0.1)',
              padding: '4px 12px',
              borderRadius: '12px',
              fontWeight: '500'
            }}>
              <Shield size={12} />
              <span>Secure & Private</span>
            </div>
            <input
              type="file"
              accept=".pdf,.docx"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
            />
          </label>
        </div>

        {/* Or divider */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          margin: '24px 0'
        }}>
          <div style={{ flex: 1, height: '1px', background: 'rgba(20, 90, 100, 0.1)' }} />
          <span style={{ fontSize: '14px', color: '#7A9CA3', fontWeight: '600' }}>OR</span>
          <div style={{ flex: 1, height: '1px', background: 'rgba(20, 90, 100, 0.1)' }} />
        </div>

        {/* Paste Text */}
        <div style={{ position: 'relative' }}>
          <textarea
            value={resumeText}
            onChange={handlePasteText}
            placeholder="Paste your resume text here..."
            style={{
              width: '100%',
              minHeight: '200px',
              padding: '16px',
              paddingTop: '40px',
              border: '2px solid rgba(20, 90, 100, 0.2)',
              borderRadius: '12px',
              fontSize: '14px',
              fontFamily: 'monospace',
              resize: 'vertical',
              outline: 'none',
              transition: 'border-color 0.3s ease'
            }}
            onFocus={(e) => e.target.style.borderColor = '#145A64'}
            onBlur={(e) => e.target.style.borderColor = 'rgba(20, 90, 100, 0.2)'}
          />
          <div style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '11px',
            color: '#059669',
            background: 'rgba(16, 185, 129, 0.1)',
            padding: '4px 10px',
            borderRadius: '10px',
            fontWeight: '500'
          }}>
            <Lock size={10} />
            <span>Stored locally only</span>
          </div>
        </div>

        {/* Job Description (Optional) */}
        <h2 style={{
          fontSize: '20px',
          fontWeight: '600',
          color: '#145A64',
          marginTop: '32px',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <TrendingUp size={20} />
          Step 2: Paste Job Description (Optional)
        </h2>

        <p style={{ fontSize: '14px', color: '#7A9CA3', marginBottom: '16px' }}>
          Adding a job description will provide keyword matching analysis and a more accurate score.
        </p>

        <textarea
          value={jobDescription}
          onChange={(e) => setJobDescription(e.target.value)}
          placeholder="Paste the job description here to see keyword matching..."
          style={{
            width: '100%',
            minHeight: '150px',
            padding: '16px',
            border: '2px solid rgba(20, 90, 100, 0.2)',
            borderRadius: '12px',
            fontSize: '14px',
            fontFamily: 'monospace',
            resize: 'vertical',
            outline: 'none',
            transition: 'border-color 0.3s ease'
          }}
          onFocus={(e) => e.target.style.borderColor = '#145A64'}
          onBlur={(e) => e.target.style.borderColor = 'rgba(20, 90, 100, 0.2)'}
        />

        {/* Error Message */}
        {error && (
          <div style={{
            marginTop: '16px',
            padding: '12px 16px',
            background: '#FEF2F2',
            border: '1px solid #FCA5A5',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            color: '#DC2626'
          }}>
            <AlertCircle size={18} />
            <span style={{ fontSize: '14px' }}>{error}</span>
          </div>
        )}

        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          gap: '12px',
          marginTop: '24px',
          flexWrap: 'wrap'
        }}>
          <button
            onClick={analyzeResume}
            disabled={isAnalyzing || !resumeText}
            style={{
              flex: 1,
              minWidth: '200px',
              background: isAnalyzing || !resumeText
                ? '#9CA3AF'
                : 'linear-gradient(135deg, #145A64 0%, #1A8A99 100%)',
              color: 'white',
              border: 'none',
              padding: '16px 32px',
              fontSize: '16px',
              fontWeight: '600',
              borderRadius: '12px',
              cursor: isAnalyzing || !resumeText ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 12px rgba(20, 90, 100, 0.2)'
            }}
          >
            {isAnalyzing ? 'Analyzing...' : 'Analyze Resume'}
          </button>

          <button
            onClick={clearAnalysis}
            style={{
              background: 'white',
              color: '#145A64',
              border: '2px solid #145A64',
              padding: '16px 32px',
              fontSize: '16px',
              fontWeight: '600',
              borderRadius: '12px',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
          >
            Clear
          </button>
        </div>

        {/* Progress Indicator */}
        {isAnalyzing && (
          <div style={{
            marginTop: '32px',
            padding: '24px',
            background: 'rgba(20, 90, 100, 0.02)',
            borderRadius: '12px',
            border: '2px solid rgba(20, 90, 100, 0.1)'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '12px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                fontSize: '15px',
                fontWeight: '600',
                color: '#145A64'
              }}>
                <div style={{
                  width: '20px',
                  height: '20px',
                  border: '3px solid #145A64',
                  borderTopColor: 'transparent',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite'
                }} />
                {currentCheck}
              </div>
              <span style={{
                fontSize: '14px',
                fontWeight: '700',
                color: '#1A8A99'
              }}>
                {analysisProgress}%
              </span>
            </div>

            {/* Progress Bar */}
            <div style={{
              width: '100%',
              height: '12px',
              background: '#E5E7EB',
              borderRadius: '6px',
              overflow: 'hidden',
              position: 'relative'
            }}>
              <div style={{
                width: `${analysisProgress}%`,
                height: '100%',
                background: 'linear-gradient(90deg, #145A64 0%, #1A8A99 100%)',
                borderRadius: '6px',
                transition: 'width 0.3s ease',
                position: 'relative',
                overflow: 'hidden'
              }}>
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 100%)',
                  animation: 'shimmer 1.5s infinite'
                }} />
              </div>
            </div>

            {/* Checks performed counter */}
            <div style={{
              marginTop: '16px',
              display: 'flex',
              justifyContent: 'center',
              gap: '24px',
              flexWrap: 'wrap',
              fontSize: '12px',
              color: '#7A9CA3'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <CheckCircle size={14} color="#10B981" />
                <span>15+ Quality Checks</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Shield size={14} color="#10B981" />
                <span>4 Scoring Categories</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <TrendingUp size={14} color="#10B981" />
                <span>Real-time Analysis</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Results Section */}
      {results && <ResultsDisplay results={results} isMobile={isMobile} />}

      {/* AI Improvement Button */}
      {results && (
        <div style={{ textAlign: 'center', margin: '32px 0' }}>
          <button
            onClick={handleImproveWithAI}
            disabled={isEnhancing}
            style={{
              padding: '16px 40px',
              background: isEnhancing ? '#ccc' : 'linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: '17px',
              fontWeight: '600',
              cursor: isEnhancing ? 'not-allowed' : 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '10px',
              boxShadow: '0 4px 12px rgba(139, 92, 246, 0.3)',
              transition: 'all 0.3s'
            }}
            onMouseEnter={(e) => {
              if (!isEnhancing) e.target.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
          >
            {isEnhancing ? (
              <><Loader size={20} style={{ animation: 'spin 1s linear infinite' }} /> Analyzing with AI...</>
            ) : (
              <><Sparkles size={20} /> Improve this Resume with AI</>
            )}
            <span style={{
              background: 'rgba(255,255,255,0.25)',
              padding: '3px 10px',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: '700'
            }}>PRO</span>
          </button>
        </div>
      )}

      {/* AI Enhancement Results */}
      {aiEnhancement && (
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: isMobile ? '24px' : '32px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          marginTop: '24px',
          border: '2px solid #8B5CF6'
        }}>
          <h3 style={{
            fontSize: '22px',
            fontWeight: '700',
            color: '#8B5CF6',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <Sparkles size={24} />
            AI-Powered Improvements
          </h3>
          <div style={{
            background: '#F9FAFB',
            padding: '20px',
            borderRadius: '12px',
            fontSize: '15px',
            lineHeight: '1.8',
            color: '#374151',
            whiteSpace: 'pre-wrap'
          }}>
            {aiEnhancement}
          </div>
        </div>
      )}

      {/* Explanation Section */}
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: isMobile ? '24px' : '32px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        marginTop: '32px',
        border: '1px solid rgba(20, 90, 100, 0.1)'
      }}>
        <button
          onClick={() => setShowExplanation(!showExplanation)}
          style={{
            width: '100%',
            background: 'none',
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            padding: '0'
          }}
        >
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#145A64',
            margin: '0',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <Info size={20} />
            How This ATS Score Is Calculated
          </h3>
          <span style={{ fontSize: '24px', color: '#145A64' }}>
            {showExplanation ? '−' : '+'}
          </span>
        </button>

        {showExplanation && <ExplanationContent isMobile={isMobile} />}
      </div>
    </div>
  );
};

// Results Display Component
const ResultsDisplay = ({ results, isMobile }) => {
  return (
    <div style={{
      background: 'white',
      borderRadius: '16px',
      padding: isMobile ? '24px' : '40px',
      boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
      border: '1px solid rgba(20, 90, 100, 0.1)',
      marginBottom: '32px'
    }}>
      {/* Score Circle */}
      <div style={{ textAlign: 'center', marginBottom: '40px' }}>
        <div style={{
          width: isMobile ? '180px' : '220px',
          height: isMobile ? '180px' : '220px',
          margin: '0 auto 24px',
          borderRadius: '50%',
          background: `conic-gradient(${results.color} ${results.percentage * 3.6}deg, #E5E7EB ${results.percentage * 3.6}deg)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          boxShadow: '0 8px 24px rgba(0,0,0,0.1)'
        }}>
          <div style={{
            width: '85%',
            height: '85%',
            borderRadius: '50%',
            background: 'white',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <div style={{ fontSize: isMobile ? '48px' : '64px', fontWeight: '700', color: results.color }}>
              {results.totalScore}
            </div>
            <div style={{ fontSize: '18px', fontWeight: '600', color: '#5A7C82' }}>
              / {results.maxScore}
            </div>
          </div>
        </div>

        <div style={{
          fontSize: isMobile ? '28px' : '36px',
          fontWeight: '700',
          color: results.color,
          marginBottom: '8px'
        }}>
          Grade: {results.grade}
        </div>

        <div style={{ fontSize: '16px', color: '#7A9CA3' }}>
          {results.percentage >= 80 ? 'Excellent! Your resume is ATS-friendly.' :
            results.percentage >= 60 ? 'Good! A few improvements will help.' :
              'Needs Improvement. Follow the suggestions below.'}
        </div>
      </div>

      {/* Breakdown */}
      <h3 style={{
        fontSize: '20px',
        fontWeight: '600',
        color: '#145A64',
        marginBottom: '20px',
        borderBottom: '2px solid rgba(20, 90, 100, 0.1)',
        paddingBottom: '12px'
      }}>
        Score Breakdown
      </h3>

      <div style={{ display: 'grid', gap: '16px', marginBottom: '32px' }}>
        <ScoreBar
          title="Keyword Matching"
          score={results.breakdown.keywordMatching.score}
          maxScore={results.breakdown.keywordMatching.maxScore}
          percentage={results.breakdown.keywordMatching.percentage}
          color="#3B82F6"
        />
        <ScoreBar
          title="Section Presence"
          score={results.breakdown.sectionPresence.score}
          maxScore={results.breakdown.sectionPresence.maxScore}
          percentage={results.breakdown.sectionPresence.percentage}
          color="#10B981"
        />
        <ScoreBar
          title="Formatting & ATS Friendliness"
          score={results.breakdown.formatting.score}
          maxScore={results.breakdown.formatting.maxScore}
          percentage={results.breakdown.formatting.percentage}
          color="#8B5CF6"
        />
        <ScoreBar
          title="Structure & Quality"
          score={results.breakdown.structure.score}
          maxScore={results.breakdown.structure.maxScore}
          percentage={results.breakdown.structure.percentage}
          color="#F59E0B"
        />
      </div>

      {/* Suggestions */}
      {results.suggestions.length > 0 && (
        <>
          <h3 style={{
            fontSize: '20px',
            fontWeight: '600',
            color: '#145A64',
            marginBottom: '20px',
            borderBottom: '2px solid rgba(20, 90, 100, 0.1)',
            paddingBottom: '12px'
          }}>
            Actionable Suggestions
          </h3>

          <div style={{ display: 'grid', gap: '12px' }}>
            {results.suggestions.map((suggestion, index) => (
              <SuggestionCard key={index} suggestion={suggestion} />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// Score Bar Component
const ScoreBar = ({ title, score, maxScore, percentage, color }) => {
  return (
    <div>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: '8px'
      }}>
        <span style={{ fontSize: '14px', fontWeight: '600', color: '#5A7C82' }}>{title}</span>
        <span style={{ fontSize: '14px', fontWeight: '700', color: color }}>
          {score}/{maxScore}
        </span>
      </div>
      <div style={{
        width: '100%',
        height: '12px',
        background: '#E5E7EB',
        borderRadius: '6px',
        overflow: 'hidden'
      }}>
        <div style={{
          width: `${percentage}%`,
          height: '100%',
          background: color,
          borderRadius: '6px',
          transition: 'width 0.5s ease'
        }} />
      </div>
    </div>
  );
};

// Suggestion Card Component
const SuggestionCard = ({ suggestion }) => {
  const priorityColors = {
    high: '#EF4444',
    medium: '#F59E0B',
    low: '#10B981'
  };

  const priorityIcons = {
    high: <AlertCircle size={18} />,
    medium: <Info size={18} />,
    low: <CheckCircle size={18} />
  };

  return (
    <div style={{
      padding: '16px',
      background: 'rgba(20, 90, 100, 0.02)',
      border: `1px solid ${priorityColors[suggestion.priority]}40`,
      borderLeft: `4px solid ${priorityColors[suggestion.priority]}`,
      borderRadius: '8px',
      display: 'flex',
      gap: '12px'
    }}>
      <div style={{ color: priorityColors[suggestion.priority], flexShrink: 0 }}>
        {priorityIcons[suggestion.priority]}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: '14px', fontWeight: '600', color: '#145A64', marginBottom: '4px' }}>
          {suggestion.text}
        </div>
        <div style={{ fontSize: '12px', color: '#7A9CA3' }}>
          {suggestion.impact} • Priority: {suggestion.priority.toUpperCase()}
        </div>
      </div>
    </div>
  );
};

// Explanation Content Component
const ExplanationContent = ({ isMobile }) => {
  const explanation = getATSScoreExplanation();

  return (
    <div style={{ marginTop: '24px' }}>
      <p style={{ fontSize: '14px', color: '#5A7C82', lineHeight: '1.6', marginBottom: '24px' }}>
        {explanation.description}
      </p>

      {explanation.breakdown.map((section, index) => (
        <div key={index} style={{ marginBottom: '24px' }}>
          <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#145A64', marginBottom: '8px' }}>
            {section.category}
          </h4>
          <p style={{ fontSize: '14px', color: '#7A9CA3', marginBottom: '12px' }}>
            {section.description}
          </p>
        </div>
      ))}

      <div style={{
        marginTop: '24px',
        padding: '16px',
        background: 'rgba(20, 90, 100, 0.05)',
        borderRadius: '8px',
        border: '1px solid rgba(20, 90, 100, 0.1)'
      }}>
        <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#145A64', marginBottom: '12px' }}>
          Privacy Guarantees:
        </h4>
        <ul style={{ margin: '0', paddingLeft: '20px', color: '#5A7C82', fontSize: '13px' }}>
          {explanation.privacy.map((item, index) => (
            <li key={index} style={{ marginBottom: '4px' }}>{item}</li>
          ))}
        </ul>
      </div>
    </div>
  );
};

// Helper function to convert resume data to text
const convertResumeToText = (resumeData) => {
  let text = '';

  if (resumeData.personal) {
    text += `${resumeData.personal.name}\n`;
    text += `${resumeData.personal.title}\n`;
    text += `${resumeData.personal.email} | ${resumeData.personal.phone}\n`;
    text += `${resumeData.personal.location}\n\n`;
  }

  if (resumeData.summary) {
    text += `Summary\n${resumeData.summary}\n\n`;
  }

  if (resumeData.experience && resumeData.experience.length > 0) {
    text += 'Experience\n';
    resumeData.experience.forEach(exp => {
      text += `${exp.title} at ${exp.company}\n`;
      text += `${exp.startDate} - ${exp.endDate}\n`;
      text += `${exp.description}\n\n`;
    });
  }

  if (resumeData.education && resumeData.education.length > 0) {
    text += 'Education\n';
    resumeData.education.forEach(edu => {
      text += `${edu.degree} at ${edu.school}\n`;
      text += `${edu.startDate} - ${edu.endDate}\n\n`;
    });
  }

  if (resumeData.skills && resumeData.skills.length > 0) {
    text += 'Skills\n';
    text += resumeData.skills.map(s => s.name).join(', ') + '\n\n';
  }

  if (resumeData.projects && resumeData.projects.length > 0) {
    text += 'Projects\n';
    resumeData.projects.forEach(proj => {
      text += `${proj.name}\n${proj.description}\n\n`;
    });
  }

  if (resumeData.certifications && resumeData.certifications.length > 0) {
    text += 'Certifications\n';
    resumeData.certifications.forEach(cert => {
      text += `${cert.name} - ${cert.issuer}\n`;
    });
  }

  return text;
};

export default ATSChecker;
